import React from 'react';
import FeatherIcon from './FeatherIcon';

// Client-side mirror of the backend impact model (donationImpact.ts): major gifts (>= $6,000) show
// dogs helped + an estimated reduced-shelter figure. Kept in sync with the certificate the donor
// receives by email / in S3.
const MAJOR_GIFT_USD = 6000;
const DOG_SPAY_USD = 600;
const SHELTER_ANIMALS_PER_SPAY = 6;

interface Props {
  /** Donation amount in dollars. */
  readonly amount: number;
  /** Donor display name; falls back to a friendly generic when absent (anonymous). */
  readonly donorName?: string | null;
}

// Exact to the cent: rounding showed a $10.61 gift as "$11".
const usd = (n: number): string => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const DonationCertificateCard: React.FC<Props> = ({ amount, donorName }) => {
  const name = (donorName ?? '').trim() || 'A generous friend of animals';
  const dogs = amount >= MAJOR_GIFT_USD ? Math.floor(amount / DOG_SPAY_USD) : 0;
  const reduced = dogs * SHELTER_ANIMALS_PER_SPAY;

  return (
    <div className="max-w-lg mx-auto">
      <div className="rounded-2xl overflow-hidden shadow-xl bg-card border border-gray-100">
        {/* Brand header band */}
        <div className="px-8 py-5 text-white" style={{ background: 'linear-gradient(135deg, #2d6a9f 0%, #4caf50 120%)' }}>
          <div className="flex items-center gap-2">
            <FeatherIcon name="award" className="text-white" />
            <span className="text-xs font-semibold tracking-widest uppercase opacity-90">Certificate of Impact</span>
          </div>
        </div>

        <div className="px-8 py-8 text-center">
          <p className="text-sm text-gray-500 mb-1">Presented to</p>
          <p className="text-xl font-bold text-primary mb-6">{name}</p>

          <p className="text-sm text-gray-500 mb-1">for a generous gift of</p>
          <p className="text-4xl font-extrabold tracking-tight mb-2" style={{ color: 'var(--tertiary-text)' }}>{usd(amount)}</p>

          {dogs > 0 ? (
            <div className="mt-6 rounded-xl bg-green-50 border border-green-100 px-5 py-4 text-left">
              <div className="text-xs font-bold tracking-widest uppercase text-secondary mb-2">Your impact</div>
              <div className="flex items-start gap-2 mb-1">
                <FeatherIcon name="heart" className="text-secondary mt-1" />
                <span className="text-gray-800 font-semibold">{dogs.toLocaleString('en-US')} dog spay/neuters funded</span>
              </div>
              <div className="flex items-start gap-2">
                <FeatherIcon name="home" className="text-tertiary mt-1" />
                <span className="text-gray-600 text-sm">~{reduced.toLocaleString('en-US')} fewer animals likely to enter shelters <span className="text-gray-400">(estimated)</span></span>
              </div>
            </div>
          ) : (
            <p className="mt-4 text-gray-600 text-sm">
              Every dollar funds spay/neuter care — and is capped to the care actually delivered, so the rest helps the next animal.
            </p>
          )}

          <div className="mt-7 flex items-center justify-center gap-2 text-xs text-gray-400">
            <FeatherIcon name="calendar" className="text-gray-400" />
            {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
          <p className="mt-3 text-sm text-gray-500">
            Thank you for giving animals a healthier, safer future. <span className="text-primary font-medium">— Pawsitive Future</span>
          </p>
        </div>
      </div>
      <p className="text-center text-xs text-gray-400 mt-3">A copy has been emailed to you and saved to your account.</p>
    </div>
  );
};

export default DonationCertificateCard;
